/*
 * FROSTBEULE · Rules (owner: PAGES) – <fb-rules> on /pages/so-gehts.
 * - arms the choreographies (class is-armed) unless html[data-motion="none"]; disarms if motion is
 *   switched off later (everything then shows its final state)
 * - [data-rl-in] blocks get .is-in once they enter the viewport (setup shuffle, award burst)
 * - timeline: rail fill follows scrolling (rAF, only while the timeline is on screen); a step turns
 *   .is-active when its number crosses 62 % of the viewport height
 * - example round: scenes play one by one when scrolled into view, the flake scoreboard follows,
 *   replay button restarts the round; scoreboard text stays in sync for screen readers
 * - card lightbox: native <dialog> + showModal (inert background, Esc), FLIP zoom from the thumbnail,
 *   ←/→ to page, focus returns to the trigger
 * Without JS every final state is visible (Liquid renders it).
 */
(function () {
  'use strict';

  if (!window.customElements || customElements.get('fb-rules')) return;

  var doc = document;
  var html = doc.documentElement;
  var EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

  var toArray = function (list) {
    return Array.prototype.slice.call(list || []);
  };

  var motion = function () {
    var C = window.FROSTBEULE;
    if (C && typeof C.motion === 'function') return C.motion();
    return html.getAttribute('data-motion') || 'full';
  };

  var clamp = function (v, a, b) {
    return Math.max(a, Math.min(b, v));
  };

  class FbRules extends HTMLElement {
    connectedCallback() {
      this.timers = [];
      this.observers = [];
      this.armed = motion() !== 'none' && 'IntersectionObserver' in window;
      if (this.armed) this.classList.add('is-armed');

      this.initInBlocks();
      this.initTimeline();
      this.initStory();
      this.initLightbox();

      this.onBlockSelect = this.onBlockSelect.bind(this);
      doc.addEventListener('shopify:block:select', this.onBlockSelect);

      if (window.MutationObserver) {
        this.motionObserver = new MutationObserver(
          function () {
            if (motion() === 'none' && this.armed) this.disarm();
          }.bind(this)
        );
        this.motionObserver.observe(html, { attributes: true, attributeFilter: ['data-motion'] });
      }
    }

    disconnectedCallback() {
      this.clearTimers();
      this.observers.forEach(function (o) {
        o.disconnect();
      });
      this.observers = [];
      if (this.motionObserver) this.motionObserver.disconnect();
      this.stopTimelineLoop();
      doc.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.lb && this.lb.dialog && this.lb.dialog.open) this.closeLightbox(true);
    }

    clearTimers() {
      this.timers.forEach(function (t) {
        clearTimeout(t);
      });
      this.timers = [];
    }

    later(fn, ms) {
      var t = setTimeout(fn, ms);
      this.timers.push(t);
      return t;
    }

    disarm() {
      this.armed = false;
      this.clearTimers();
      this.stopTimelineLoop();
      this.classList.remove('is-armed');
      if (this.fill) this.fill.style.transform = '';
      toArray(this.querySelectorAll('[data-rl-in], [data-rl-scene]')).forEach(function (el) {
        el.classList.add('is-in');
      });
      (this.steps || []).forEach(function (s) {
        s.classList.add('is-active');
      });
      if (this.story) this.applyBoard(this.scenes.length - 1);
    }

    /* ---------- blocks that play once ---------- */
    initInBlocks() {
      var blocks = toArray(this.querySelectorAll('[data-rl-in]'));
      if (!this.armed) {
        blocks.forEach(function (b) {
          b.classList.add('is-in');
        });
        return;
      }
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          });
        },
        { threshold: 0.3 }
      );
      blocks.forEach(function (b) {
        io.observe(b);
      });
      this.observers.push(io);
    }

    /* ---------- timeline ---------- */
    initTimeline() {
      this.timeline = this.querySelector('[data-rl-timeline]');
      if (!this.timeline) return;
      this.fill = this.timeline.querySelector('[data-rl-fill]');
      this.steps = toArray(this.timeline.querySelectorAll('[data-rl-step]'));
      this.nodes = this.steps.map(function (s) {
        return s.querySelector('.fb-rules__node') || s;
      });
      if (!this.armed) {
        this.steps.forEach(function (s) {
          s.classList.add('is-active');
        });
        return;
      }
      this.tlRaf = 0;
      this.tlListening = false;
      this.onTlScroll = function () {
        if (!this.tlRaf) this.tlRaf = window.requestAnimationFrame(this.updateTimeline.bind(this));
      }.bind(this);

      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(
            function (e) {
              if (e.isIntersecting) this.startTimelineLoop();
              else this.stopTimelineLoop();
            }.bind(this)
          );
        }.bind(this),
        { rootMargin: '10% 0px 10% 0px' }
      );
      io.observe(this.timeline);
      this.observers.push(io);
      this.updateTimeline();
    }

    startTimelineLoop() {
      if (this.tlListening) return;
      this.tlListening = true;
      window.addEventListener('scroll', this.onTlScroll, { passive: true });
      window.addEventListener('resize', this.onTlScroll, { passive: true });
      this.onTlScroll();
    }

    stopTimelineLoop() {
      if (!this.tlListening) return;
      this.tlListening = false;
      window.removeEventListener('scroll', this.onTlScroll);
      window.removeEventListener('resize', this.onTlScroll);
      if (this.tlRaf) window.cancelAnimationFrame(this.tlRaf);
      this.tlRaf = 0;
      this.updateTimeline();
    }

    updateTimeline() {
      this.tlRaf = 0;
      if (!this.armed || !this.timeline) return;
      var line = window.innerHeight * 0.62;
      var rail = this.fill ? this.fill.parentElement.getBoundingClientRect() : null;
      var states = this.nodes.map(function (n) {
        var r = n.getBoundingClientRect();
        return r.top + r.height / 2 <= line;
      });
      if (rail && this.fill) {
        var p = rail.height ? clamp((line - rail.top) / rail.height, 0, 1) : 0;
        this.fill.style.transform = 'scaleY(' + p.toFixed(4) + ')';
      }
      this.steps.forEach(function (s, i) {
        s.classList.toggle('is-active', states[i]);
      });
    }

    /* ---------- example round ---------- */
    initStory() {
      this.story = this.querySelector('[data-rl-story]');
      if (!this.story) return;
      this.scenes = toArray(this.story.querySelectorAll('[data-rl-scene]'));
      this.live = this.querySelector('[data-rl-live]');
      this.seats = {};
      toArray(this.story.querySelectorAll('[data-rl-seat]')).forEach(
        function (el) {
          this.seats[el.getAttribute('data-rl-seat')] = {
            el: el,
            dots: toArray(el.querySelectorAll('.fb-flakes__dot')),
            sr: el.querySelector('[data-rl-seat-sr]'),
          };
        }.bind(this)
      );
      this.tplOne = this.story.getAttribute('data-tpl-one') || '1';
      this.tplOther = this.story.getAttribute('data-tpl-other') || '7';
      this.tplFrozen = this.story.getAttribute('data-tpl-frozen') || '';

      if (!this.armed || !this.scenes.length) return;

      this.revealed = -1;
      this.applyBoard(-1);

      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(
            function (e) {
              if (!e.isIntersecting || this.replaying) return;
              var i = this.scenes.indexOf(e.target);
              io.unobserve(e.target);
              this.playScene(i);
            }.bind(this)
          );
        }.bind(this),
        { threshold: 0.35, rootMargin: '0px 0px -12% 0px' }
      );
      this.scenes.forEach(function (s) {
        io.observe(s);
      });
      this.observers.push(io);
      this.storyIO = io;

      this.replayBtn = this.story.querySelector('[data-rl-replay]');
      if (this.replayBtn) {
        this.replayBtn.hidden = false;
        this.replayBtn.addEventListener('click', this.replay.bind(this));
      }
    }

    playScene(i) {
      var scene = this.scenes[i];
      if (!scene || scene.classList.contains('is-in')) return;
      scene.classList.add('is-in');
      this.later(
        function () {
          this.revealed = Math.max(this.revealed, i);
          this.applyBoard(this.revealed);
        }.bind(this),
        motion() === 'none' ? 0 : 1750
      );
    }

    replay() {
      this.clearTimers();
      if (this.storyIO) this.storyIO.disconnect();
      this.replaying = true;
      this.scenes.forEach(function (s) {
        s.classList.remove('is-in');
      });
      this.revealed = -1;
      this.applyBoard(-1);
      void this.story.offsetWidth;
      if (this.live) this.live.textContent = this.story.getAttribute('data-tpl-replay') || '';
      var gap = 2300;
      this.scenes.forEach(
        function (s, i) {
          this.later(
            function () {
              this.playScene(i);
              if (i === this.scenes.length - 1) {
                this.later(
                  function () {
                    this.replaying = false;
                  }.bind(this),
                  1800
                );
              }
            }.bind(this),
            300 + i * gap
          );
        }.bind(this)
      );
    }

    applyBoard(uptoIndex) {
      var state = {};
      Object.keys(this.seats).forEach(function (name) {
        state[name] = { n: 0, frozen: false };
      });
      for (var i = 0; i <= uptoIndex && i < this.scenes.length; i += 1) {
        var s = this.scenes[i];
        var who = s.getAttribute('data-flake-player');
        if (!who || !state[who]) continue;
        state[who].n = parseInt(s.getAttribute('data-flake-total'), 10) || 0;
        if (s.getAttribute('data-result') === 'freeze') state[who].frozen = true;
      }
      Object.keys(this.seats).forEach(
        function (name) {
          var seat = this.seats[name];
          var st = state[name];
          seat.dots.forEach(function (d, k) {
            d.classList.toggle('is-on', st.n > k);
          });
          seat.el.classList.toggle('is-frozen', st.frozen);
          if (seat.sr) {
            var txt = st.n === 1 ? this.tplOne : this.tplOther.replace('7', String(st.n));
            if (st.frozen && this.tplFrozen) txt += ', ' + this.tplFrozen.toLowerCase();
            seat.sr.textContent = txt;
          }
        }.bind(this)
      );
    }

    /* ---------- lightbox ---------- */
    initLightbox() {
      var dialog = this.querySelector('[data-rl-lb]');
      var items = toArray(this.querySelectorAll('[data-rl-zoom]'));
      if (!dialog || !items.length) return;
      var lb = (this.lb = {
        dialog: dialog,
        items: items,
        img: dialog.querySelector('[data-rl-lb-img]'),
        caption: dialog.querySelector('[data-rl-lb-caption]'),
        status: dialog.querySelector('[data-rl-lb-status]'),
        prev: dialog.querySelector('[data-rl-lb-prev]'),
        next: dialog.querySelector('[data-rl-lb-next]'),
        closeBtn: dialog.querySelector('.fb-rules-lb__close'),
        scrim: dialog.querySelector('.fb-rules-lb__scrim'),
        index: 0,
        trigger: null,
        closing: false,
      });
      this.statusTpl = dialog.getAttribute('data-tpl-status') || '';

      items.forEach(
        function (btn, i) {
          btn.addEventListener(
            'click',
            function () {
              this.openLightbox(i, btn);
            }.bind(this)
          );
        }.bind(this)
      );
      toArray(dialog.querySelectorAll('[data-rl-lb-close]')).forEach(
        function (el) {
          el.addEventListener(
            'click',
            function () {
              this.closeLightbox();
            }.bind(this)
          );
        }.bind(this)
      );
      if (lb.prev)
        lb.prev.addEventListener(
          'click',
          function () {
            this.stepLightbox(-1);
          }.bind(this)
        );
      if (lb.next)
        lb.next.addEventListener(
          'click',
          function () {
            this.stepLightbox(1);
          }.bind(this)
        );
      dialog.addEventListener(
        'cancel',
        function (e) {
          e.preventDefault();
          this.closeLightbox();
        }.bind(this)
      );
      dialog.addEventListener(
        'keydown',
        function (e) {
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.stepLightbox(-1);
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.stepLightbox(1);
          } else if (e.key === 'Escape' && typeof dialog.showModal !== 'function') {
            this.closeLightbox();
          }
        }.bind(this)
      );
      var multi = items.length > 1;
      if (lb.prev) lb.prev.hidden = !multi;
      if (lb.next) lb.next.hidden = !multi;
    }

    fillLightbox(i) {
      var lb = this.lb;
      var item = lb.items[i];
      lb.index = i;
      lb.img.src = item.getAttribute('data-src');
      lb.img.alt = item.getAttribute('data-name') || '';
      if (lb.caption) lb.caption.textContent = item.getAttribute('data-name') || '';
      if (lb.status && this.statusTpl) {
        lb.status.textContent = this.statusTpl
          .replace('__C__', String(i + 1))
          .replace('__T__', String(lb.items.length))
          .replace('__N__', item.getAttribute('data-name') || '');
      }
    }

    openLightbox(i, trigger) {
      var lb = this.lb;
      if (!lb || lb.dialog.open) return;
      lb.trigger = trigger;
      lb.closing = false;
      this.fillLightbox(i);
      if (typeof lb.dialog.showModal === 'function') lb.dialog.showModal();
      else lb.dialog.setAttribute('open', '');
      this.lockScroll(true);
      if (lb.closeBtn) lb.closeBtn.focus({ preventScroll: true });

      var m = motion();
      if (m === 'none' || !lb.img.animate) return;
      var thumb = trigger && trigger.querySelector('img');
      var to = lb.img.getBoundingClientRect();
      if (m === 'full' && thumb && to.width) {
        var from = thumb.getBoundingClientRect();
        var dx = from.left + from.width / 2 - (to.left + to.width / 2);
        var dy = from.top + from.height / 2 - (to.top + to.height / 2);
        var s = from.width / to.width;
        lb.img.animate(
          [{ transform: 'translate(' + dx + 'px,' + dy + 'px) scale(' + s + ')' }, { transform: 'none' }],
          { duration: 560, easing: EASE }
        );
      } else {
        lb.img.animate([{ opacity: 0, transform: 'scale(0.97)' }, { opacity: 1, transform: 'none' }], {
          duration: 260,
          easing: EASE,
        });
      }
      if (lb.scrim) lb.scrim.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 320, easing: 'ease-out' });
      [lb.caption, lb.closeBtn, lb.prev, lb.next].forEach(function (el) {
        if (el && el.animate)
          el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 260, delay: 220, easing: 'ease-out', fill: 'backwards' });
      });
    }

    stepLightbox(dir) {
      var lb = this.lb;
      if (!lb || !lb.dialog.open || lb.items.length < 2) return;
      var next = (lb.index + dir + lb.items.length) % lb.items.length;
      this.fillLightbox(next);
      lb.trigger = lb.items[next];
      if (motion() !== 'none' && lb.img.animate) {
        var off = motion() === 'full' ? dir * 28 : 0;
        lb.img.animate(
          [{ opacity: 0, transform: 'translateX(' + off + 'px)' }, { opacity: 1, transform: 'none' }],
          { duration: 280, easing: EASE }
        );
      }
    }

    closeLightbox(immediate) {
      var lb = this.lb;
      if (!lb || !lb.dialog.open || lb.closing) return;
      lb.closing = true;
      var done = function () {
        lb.closing = false;
        if (typeof lb.dialog.close === 'function') lb.dialog.close();
        else lb.dialog.removeAttribute('open');
        this.lockScroll(false);
        if (lb.trigger && !immediate) lb.trigger.focus({ preventScroll: true });
      }.bind(this);

      var m = motion();
      if (immediate || m === 'none' || !lb.img.animate) return done();
      var thumb = lb.trigger && lb.trigger.querySelector('img');
      var to = lb.img.getBoundingClientRect();
      var from = thumb ? thumb.getBoundingClientRect() : null;
      var visible = from && from.bottom > 0 && from.top < window.innerHeight && from.width > 0;
      var anim;
      if (m === 'full' && visible && to.width) {
        var dx = from.left + from.width / 2 - (to.left + to.width / 2);
        var dy = from.top + from.height / 2 - (to.top + to.height / 2);
        anim = lb.img.animate(
          [{ transform: 'none' }, { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(' + from.width / to.width + ')' }],
          { duration: 420, easing: EASE, fill: 'forwards' }
        );
      } else {
        anim = lb.img.animate([{ opacity: 1 }, { opacity: 0, transform: 'scale(0.97)' }], {
          duration: 200,
          easing: 'ease-out',
          fill: 'forwards',
        });
      }
      [lb.scrim, lb.caption, lb.closeBtn, lb.prev, lb.next].forEach(function (el) {
        if (el && el.animate) el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 240, easing: 'ease-out', fill: 'forwards' });
      });
      anim.onfinish = function () {
        done();
        [lb.img, lb.scrim, lb.caption, lb.closeBtn, lb.prev, lb.next].forEach(function (el) {
          if (el && el.getAnimations)
            el.getAnimations().forEach(function (a) {
              a.cancel();
            });
        });
      };
    }

    lockScroll(on) {
      var C = window.FROSTBEULE;
      if (C && typeof C.lockScroll === 'function' && typeof C.unlockScroll === 'function') {
        if (on) C.lockScroll();
        else C.unlockScroll();
        return;
      }
      html.style.overflow = on ? 'hidden' : '';
    }

    /* ---------- theme editor ---------- */
    onBlockSelect(e) {
      if (!this.contains(e.target)) return;
      var t = e.target;
      if (t.hasAttribute('data-rl-scene')) {
        var i = this.scenes.indexOf(t);
        for (var k = 0; k <= i; k += 1) this.scenes[k].classList.add('is-in');
        this.revealed = Math.max(this.revealed || -1, i);
        this.applyBoard(this.revealed);
      }
      if (t.hasAttribute('data-rl-in')) t.classList.add('is-in');
      toArray(t.querySelectorAll('[data-rl-step]')).forEach(function (s) {
        s.classList.add('is-active');
      });
      toArray(t.querySelectorAll('[data-rl-scene]')).forEach(function (s) {
        s.classList.add('is-in');
      });
      if (t.querySelector('[data-rl-scene]') && this.scenes) this.applyBoard(this.scenes.length - 1);
    }
  }

  customElements.define('fb-rules', FbRules);
})();
